import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface TavusConversation {
  conversation_id: string;
  conversation_name?: string;
  conversation_url: string;
  status: string;
  callback_url?: string;
  created_at?: string;
  meeting_token?: string;
}

export interface StartConversationOptions {
  conversationName?: string;
  context?: string;
  testMode?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class TavusCviService {
  private readonly apiUrl = 'https://tavusapi.com/v2/conversations';
  public readonly apiKey = (environment as any).tavusApiKey || 'b3b8695f2d6a4c54a5ddca5b603aa774';
  public readonly palId = (environment as any).tavusPalId || 'paa0abc1c5db';
  public readonly faceId = (environment as any).tavusFaceId || 'r291e545fd67';

  private currentConversationSubject = new BehaviorSubject<TavusConversation | null>(null);
  public currentConversation$: Observable<TavusConversation | null> = this.currentConversationSubject.asObservable();

  private isConnectingSubject = new BehaviorSubject<boolean>(false);
  public isConnecting$: Observable<boolean> = this.isConnectingSubject.asObservable();

  private errorSubject = new BehaviorSubject<string | null>(null);
  public error$: Observable<string | null> = this.errorSubject.asObservable();

  get currentConversation(): TavusConversation | null {
    return this.currentConversationSubject.value;
  }

  /**
   * Starts a new Tavus CVI conversation with the configured PAL and Face
   */
  async startConversation(options?: StartConversationOptions): Promise<TavusConversation> {
    const existing = this.currentConversationSubject.value;
    if (existing && existing.status === 'active' && existing.conversation_url) {
      return existing;
    }

    this.isConnectingSubject.next(true);
    this.errorSubject.next(null);

    try {
      const payload: Record<string, any> = {
        pal_id: this.palId,
        face_id: this.faceId,
        conversation_name: options?.conversationName || 'Mihuru AI Travel Assistant',
        test_mode: options?.testMode ?? false,
        properties: {
          max_call_duration: 420, // 7 minutes
          participant_left_timeout: 60,
          participant_absent_timeout: 300,
        },
      };

      if (options?.context) {
        payload.conversational_context = options.context;
      }

      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': this.apiKey,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMsg = data?.message || data?.error || `Tavus API Error (${response.status})`;
        throw new Error(errorMsg);
      }

      this.currentConversationSubject.next(data);
      this.isConnectingSubject.next(false);
      return data;
    } catch (err: any) {
      const message = err?.message || 'Unable to connect to Tavus AI service. Please try again.';
      this.errorSubject.next(message);
      this.isConnectingSubject.next(false);
      throw err;
    }
  }

  /**
   * Explicitly ends an active conversation and frees resources
   */
  async endConversation(conversationId?: string): Promise<boolean> {
    const targetId = conversationId || this.currentConversationSubject.value?.conversation_id;
    if (!targetId) {
      this.currentConversationSubject.next(null);
      this.isConnectingSubject.next(false);
      return true;
    }

    try {
      await fetch(`${this.apiUrl}/${targetId}/end`, {
        method: 'POST',
        headers: {
          'x-api-key': this.apiKey,
        },
        keepalive: true,
      });
      return true;
    } catch (err) {
      console.warn('Error ending Tavus conversation:', err);
      return false;
    } finally {
      this.currentConversationSubject.next(null);
      this.isConnectingSubject.next(false);
    }
  }
}
